import React, { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Spinner from "react-bootstrap/Spinner";
import Tooltip from "react-bootstrap/Tooltip";

import {
  getAccountWhatsAppConsent,
  setAccountWhatsAppConsent,
} from "../../whatsappApi";
import { whatsAppFeatureEnabled } from "./access";

/**
 * AccountWhatsAppConsent — the account holder's own WhatsApp opt-in.
 *
 * Separate from WhatsAppConsentToggle, and separate in the database, because
 * they answer different questions. A patient's consent covers messages about
 * their care; this covers messages about the ACCOUNT — statements, balances,
 * payment links. A parent agreeing to discuss a bill has not agreed to
 * clinical messages about their child, and one switch for both would quietly
 * make that claim. The send path reads whichever subject a thread is linked to
 * and never substitutes one for the other.
 *
 * Fetches its own state rather than taking it from the account form: consent
 * is saved immediately on toggle, not with the rest of the form, so that a
 * withdrawal takes effect the moment someone asks for it rather than when the
 * user remembers to press Save.
 *
 * Renders nothing when the practice is not on a tier that includes WhatsApp.
 */
export default function AccountWhatsAppConsent({ accountId, disabled }) {
  const [consent, setConsent] = useState(null);
  const [cellNumber, setCellNumber] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!accountId) {
      setLoading(false);
      return undefined;
    }

    (async () => {
      try {
        const data = await getAccountWhatsAppConsent(accountId);
        if (cancelled) return;
        setConsent(data.whatsappConsent || null);
        setCellNumber(data.cellNumber || null);
      } catch (_err) {
        if (!cancelled) setError("Could not load consent.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accountId]);

  const toggle = async (next) => {
    const previous = consent;
    setConsent({ ...(consent || {}), granted: next });
    setSaving(true);
    setError(null);
    try {
      const data = await setAccountWhatsAppConsent(accountId, next, "practice");
      setConsent(data.whatsappConsent || { granted: next });
    } catch (err) {
      // Put the switch back rather than leaving the UI claiming a consent
      // state the server does not hold.
      setConsent(previous);
      setError(
        err?.response?.data?.message || "Could not save consent. Try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!whatsAppFeatureEnabled()) return null;
  if (!accountId) return null;

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2">
        <Spinner size="sm" animation="border" variant="primary" />
        <span className="small text-muted">Loading WhatsApp consent…</span>
      </div>
    );
  }

  const granted = consent?.granted === true;
  const capturedAt =
    granted && consent?.grantedAt
      ? new Date(consent.grantedAt).toLocaleDateString("en-GB")
      : null;

  return (
    <div>
      <div className="d-flex align-items-center gap-2">
        <Form.Check
          type="switch"
          id={`wa-account-consent-${accountId}`}
          checked={granted}
          disabled={disabled || saving}
          onChange={(e) => toggle(e.target.checked)}
          label="WhatsApp messages"
        />

        {saving && <Spinner size="sm" animation="border" />}

        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              {capturedAt
                ? `Consent recorded on ${capturedAt}. The account holder may opt out at any time by replying STOP.`
                : "The account holder must agree before PractoSync can send statements or account messages on WhatsApp."}
            </Tooltip>
          }
        >
          <i className="fa-solid fa-circle-info text-primary" />
        </OverlayTrigger>
      </div>

      {/* Name the number being consented on behalf of. Without it the switch
          asks the user to trust that the right mobile is on file. */}
      {granted && !cellNumber && (
        <small className="text-danger d-block mt-1">
          No mobile number on this account — add one before messages can be sent.
        </small>
      )}
      {granted && cellNumber && (
        <small className="text-muted d-block mt-1">Messages go to {cellNumber}.</small>
      )}
      {error && <small className="text-danger d-block mt-1">{error}</small>}
    </div>
  );
}
